import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import {
  loadExploreProfiles,
  saveLike,
  saveDislike,
  checkDailyLikeLimit,
  checkDailySuperLikeLimit,
  incrementDailyLikeCount,
  incrementDailySuperLikeCount,
  getSuperLikeResetTimeString
} from '../lib/explore'
import { getExistingChat, getOrCreateChat } from '../lib/chats'
import PersonCard from './PersonCard'
import MatchModal from './MatchModal'
import CompatibilityModal from './CompatibilityModal'
import Loader from './Loader'
import './People.css'

const People = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, profile } = useAuth()
  
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [cardAnimation, setCardAnimation] = useState('')
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [matchedProfile, setMatchedProfile] = useState(null)
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false)
  const [compatibilityProfile, setCompatibilityProfile] = useState(null)
  const [likeLimit, setLikeLimit] = useState({ limit: 5, used: 0, remaining: 5, canLike: true })
  const [superLikeLimit, setSuperLikeLimit] = useState({ limit: 0, used: 0, remaining: 0, canSuperLike: false })

  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'

  // Только id и looking_for: при TOKEN_REFRESHED приходят новые ссылки user/profile — иначе лишний loadProfiles + Loader
  useEffect(() => {
    if (user?.id) {
      const currentProfile = profile || { id: user.id, looking_for: 'both' }
      const lookingFor = currentProfile.looking_for || 'both'
      console.log('[People] Loading explore profiles with looking_for:', lookingFor)
      loadProfiles()
      checkLimits()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно не [user, profile] (ссылки)
  }, [user?.id, profile?.looking_for])

  const loadProfiles = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      // Используем профиль или минимальный
      const currentProfile = profile || { id: user.id, looking_for: 'both' }
      const lookingFor = currentProfile.looking_for || 'both'
      
      console.log('[People] Loading profiles with:', {
        userId: user.id,
        profileLookingFor: profile?.looking_for,
        currentProfileLookingFor: currentProfile.looking_for,
        usingLookingFor: lookingFor
      })
      
      const loadedProfiles = await loadExploreProfiles(user.id, lookingFor)
      console.log('[People] ✅ Loaded', loadedProfiles.length, 'profiles')
      
      if (loadedProfiles.length === 0) {
        console.warn('[People] ⚠️ No profiles loaded. Check console for details.')
      }
      
      setProfiles(loadedProfiles)
      setCurrentIndex(0)
    } catch (error) {
      console.error('[People] ❌ Error loading profiles:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkLimits = async () => {
    if (!user) return
    
    // Используем профиль или минимальный
    const currentProfile = profile || { id: user.id, timezone: 'UTC' }
    const timezone = currentProfile.timezone || 'UTC'
    const likeLimitData = await checkDailyLikeLimit(user.id, timezone)
    const superLikeLimitData = await checkDailySuperLikeLimit(user.id, timezone)
    
    setLikeLimit(likeLimitData)
    setSuperLikeLimit(superLikeLimitData)
  }

  const handleDislike = async () => {
    if (!user || currentIndex >= profiles.length) return
    
    const currentProfile = profiles[currentIndex]
    if (!currentProfile) return

    trackEvent('card_disliked')
    setCardAnimation('slide-left')
    
    try {
      await saveDislike(user.id, currentProfile.id)
      
      setTimeout(() => {
        setCardAnimation('')
        if (currentIndex < profiles.length - 1) {
          setCurrentIndex(prev => prev + 1)
        } else {
          // Профили закончились
          setCurrentIndex(profiles.length)
        }
      }, 300)
    } catch (error) {
      console.error('Error disliking:', error)
      setCardAnimation('')
    }
  }

  const handleLike = async () => {
    if (!user || currentIndex >= profiles.length) return
    
    const currentProfile = profiles[currentIndex]
    if (!currentProfile) return

    // Проверяем лимит
    if (!likeLimit.canLike) {
      trackEvent('paywall_shown', { reason: 'like_limit_reached' })
      navigate('/paywall?reason=like_limit_reached', { state: { from: location.pathname } })
      return
    }

    trackEvent('card_liked')
    setCardAnimation('slide-right')
    
    try {
      const timezone = userProfile.timezone || 'UTC'
      console.log('[People] Liking profile:', currentProfile.id)
      const { match, error: likeError } = await saveLike(user.id, currentProfile.id, false)
      
      if (likeError) {
        console.error('[People] Error saving like:', likeError)
        setCardAnimation('')
        return
      }
      
      console.log('[People] Like saved, match:', match)
      
      // Увеличиваем счетчик лайков
      await incrementDailyLikeCount(user.id, timezone)
      await checkLimits()
      
      setTimeout(() => {
        setCardAnimation('')
        
        if (match) {
          console.log('[People] ✅ Match detected! Showing match modal')
          // Показываем модалку матча
          setMatchedProfile(currentProfile)
          setShowMatchModal(true)
        } else {
          // Переходим к следующему профилю
          if (currentIndex < profiles.length - 1) {
            setCurrentIndex(prev => prev + 1)
          } else {
            setCurrentIndex(profiles.length)
          }
        }
      }, 300)
    } catch (error) {
      console.error('Error liking:', error)
      setCardAnimation('')
    }
  }

  const handleSuperLike = async () => {
    if (!user || currentIndex >= profiles.length) return
    
    const currentProfile = profiles[currentIndex]
    if (!currentProfile) return

    // Проверяем подписку/пол для суперлайка
    const hasSubscription = userProfile.subscription_status === 'active' || userProfile.subscription_status === 'trial'
    const isFemale = userProfile.gender === 'female'
    
    if (!hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'super_like_no_access' })
      navigate('/paywall?reason=super_like_no_access', { state: { from: location.pathname } })
      return
    }

    // Проверяем лимит суперлайков
    if (!superLikeLimit.canSuperLike) {
      trackEvent('super_like_limit_reached', { from: 'explore' })
      const timezone = userProfile.timezone || 'UTC'
      const resetIn = getSuperLikeResetTimeString(timezone)
      alert(t('people.superLikeLimit', { resetIn }))
      return
    }

    trackEvent('star_button_clicked')
    setCardAnimation('slide-right')
    
    try {
      const timezone = userProfile.timezone || 'UTC'
      console.log('[People] Super liking profile:', currentProfile.id)
      const { match, error: superLikeError } = await saveLike(user.id, currentProfile.id, true)
      
      if (superLikeError) {
        console.error('[People] Error saving super like:', superLikeError)
        setCardAnimation('')
        return
      }
      
      console.log('[People] Super like saved, match:', match)
      
      // Увеличиваем счетчик суперлайков
      await incrementDailySuperLikeCount(user.id, timezone)
      await checkLimits()
      
      setTimeout(() => {
        setCardAnimation('')
        
        if (match) {
          console.log('[People] ✅ Match detected! Showing match modal')
          setMatchedProfile(currentProfile)
          setShowMatchModal(true)
        } else {
          if (currentIndex < profiles.length - 1) {
            setCurrentIndex(prev => prev + 1)
          } else {
            setCurrentIndex(profiles.length)
          }
        }
      }, 300)
    } catch (error) {
      console.error('Error super liking:', error)
      setCardAnimation('')
    }
  }

  const handleChat = async () => {
    if (!user || currentIndex >= profiles.length) return
    
    const currentProfile = profiles[currentIndex]
    if (!currentProfile) return

    const hasSubscription = userProfile.subscription_status === 'active' || userProfile.subscription_status === 'trial'
    const isFemale = userProfile.gender === 'female'
    
    if (!hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'chat_no_match' })
      navigate('/paywall?reason=chat_no_match', { state: { from: location.pathname } })
      return
    }

    trackEvent('chat_opened', { from: 'explore' })
    const { data: chat, error: chatError } = await getOrCreateChat(user.id, currentProfile.id, null)
    if (chatError || !chat) return
    navigate(`/chat/${chat.id}`)
  }

  const handleSeeCompatibility = () => {
    if (!user || currentIndex >= profiles.length) return
    
    const currentProfile = profiles[currentIndex]
    if (!currentProfile) return

    // Проверяем доступ к деталям совместимости
    const hasSubscription = userProfile.subscription_status === 'active' || userProfile.subscription_status === 'trial'
    const isFemale = userProfile.gender === 'female'
    
    if (!hasSubscription) {
      trackEvent('paywall_shown', { reason: 'compatibility_details' })
      navigate('/paywall?reason=compatibility_details', { state: { from: location.pathname } })
      return
    }

    setCompatibilityProfile(currentProfile)
    setShowCompatibilityModal(true)
    trackEvent('see_how_you_align_clicked')
  }

  const handleGetAstroPlus = () => {
    trackEvent('get_astro_plus_clicked', { location: 'people' })
    navigate('/paywall?reason=subscription', { state: { from: location.pathname } })
  }

  const handleMatchModalClose = async () => {
    setShowMatchModal(false)
    setMatchedProfile(null)
    
    // Удаляем заматченный профиль из списка
    if (matchedProfile) {
      setProfiles(prev => prev.filter(p => p.id !== matchedProfile.id))
    }
    
    // Переходим к следующему профилю или перезагружаем список
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(prev => prev + 1)
    } else {
      // Если это был последний профиль, перезагружаем список
      await loadProfiles()
    }
  }

  const handleMatchModalChat = async () => {
    setShowMatchModal(false)
    const matched = matchedProfile
    setMatchedProfile(null)
    
    if (!user || !matched) return
    
    setProfiles(prev => prev.filter(p => p.id !== matched.id))
    trackEvent('match_chat_started')
    
    const { data: existingChat } = await getExistingChat(user.id, matched.id)
    if (existingChat) {
      navigate(`/chat/${existingChat.id}`)
    } else {
      navigate(`/chat/new/${matched.id}`)
    }
    
    if (currentIndex >= profiles.length - 1) {
      await loadProfiles()
    } else {
      setCurrentIndex(prev => prev + 1)
    }
  }

  const isEndOfFeed = currentIndex >= profiles.length
  const currentPerson = profiles[currentIndex]

  // Если нет пользователя, не показываем ничего (PersonalArea обработает редирект)
  if (!user) {
    return null
  }

  // Используем минимальный профиль если его нет
  const userProfile = profile || { id: user.id, looking_for: 'both' }

  if (loading) {
    return (
      <div className="people-screen">
        <div className="people-top-bar">
          <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
            <img src="/Logo.png" alt="AstroMatch Logo" />
          </button>
          {!hasSubscription && (
            <button className="astro-plus-btn" onClick={handleGetAstroPlus}>
              {t('people.getAstroPlus')}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
          <Loader text={t('people.loadingProfiles')} />
        </div>
      </div>
    )
  }

  return (
    <div className="people-screen">
      <div className="people-top-bar">
        <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
          <img src="/Logo.png" alt="AstroMatch Logo" />
        </button>
        {!hasSubscription && (
          <button className="astro-plus-btn" onClick={handleGetAstroPlus}>
            {t('people.getAstroPlus')}
          </button>
        )}
      </div>

      {isEndOfFeed ? (
        <div className="end-of-feed">
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>✨</div>
          <h2 className="end-title">{t('people.noMoreProfiles')}</h2>
          <p className="end-description">
            {t('people.noMoreProfilesDesc')}
          </p>
        </div>
      ) : (
        <>
          {currentPerson && (
            <PersonCard
              person={currentPerson}
              onSeeCompatibility={handleSeeCompatibility}
              className={cardAnimation}
            />
          )}

          <div className="action-buttons">
            <button className="action-btn star-btn" onClick={handleSuperLike}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
            </button>
            <button className="action-btn dislike-btn" onClick={handleDislike}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <button className="action-btn like-btn" onClick={handleLike}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
            <button className="action-btn chat-btn" onClick={handleChat}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </button>
          </div>
        </>
      )}

      {showMatchModal && matchedProfile && (
        <MatchModal
          matchedProfile={matchedProfile}
          onClose={handleMatchModalClose}
          onStartChatting={handleMatchModalChat}
        />
      )}

      {showCompatibilityModal && compatibilityProfile && (
        <CompatibilityModal
          profile={compatibilityProfile}
          fullContent={userProfile?.subscription_status === 'active' || userProfile?.subscription_status === 'trial'}
          currentProfile={userProfile}
          onClose={() => {
            setShowCompatibilityModal(false)
            setCompatibilityProfile(null)
          }}
        />
      )}
    </div>
  )
}

export default People
